import {
  Sparkles,
  ListChecks,
  FolderKanban,
  Calendar,
  Flame,
  Coffee,
  FileText,
  Users,
  Bot,
  Plug,
} from 'lucide-react';
import { GOOGLE_APP_INTEGRATIONS } from '../lib/constants';

// Stable IDs for sections and steps — these keys are persisted in
// UserSettings.tutorial_progress on the backend. Reordering or renaming an ID
// would orphan past progress, so add new ones at the end and never recycle.
export const TUTORIAL_SECTIONS = [
  {
    id: 'getting_started',
    icon: Sparkles,
    steps: ['intro', 'sidebar', 'search'],
  },
  {
    id: 'tasks',
    icon: ListChecks,
    steps: ['quick_add', 'nlp', 'labels'],
  },
  {
    id: 'projects_views',
    icon: FolderKanban,
    steps: ['board_list', 'calendar_gallery'],
  },
  {
    id: 'planner',
    icon: Calendar,
    steps: ['drag_drop', 'auto_schedule'],
  },
  {
    id: 'habits',
    icon: Flame,
    steps: ['create', 'streaks'],
  },
  {
    id: 'focus_time',
    icon: Coffee,
    steps: ['what_is', 'protect'],
  },
  {
    id: 'documents',
    icon: FileText,
    steps: ['notes', 'mindmaps'],
  },
  {
    id: 'teams',
    icon: Users,
    steps: ['invite', 'assign'],
  },
  {
    id: 'ai_assistant',
    icon: Bot,
    steps: ['chat', 'parse'],
  },
  // Its only steps are Gmail and Google Calendar, hidden with GOOGLE_APP_INTEGRATIONS.
  ...(GOOGLE_APP_INTEGRATIONS ? [{
    id: 'integrations',
    icon: Plug,
    steps: ['google_calendar', 'gmail'],
  }] : []),
];

// The section each answer to onboarding's "What will you use BusyBee.Day for?" points to.
const PERSONA_SECTIONS = {
  personal_tasks: 'tasks',
  habits: 'habits',
  focus: 'focus_time',
  team: 'teams',
  planning: 'planner',
  bots: 'ai_assistant',
};

/** The sections in the order the Tutorials list shows them: Getting started, then the ones
 * the person's onboarding answers (UserSettings.persona) point to, then the rest. */
export const sectionsFor = (persona) => {
  const wanted = new Set((persona || []).map((p) => PERSONA_SECTIONS[p]).filter(Boolean));
  const [first, ...rest] = TUTORIAL_SECTIONS;
  return [first, ...rest.filter((s) => wanted.has(s.id)), ...rest.filter((s) => !wanted.has(s.id))];
};

// Coachmark metadata: where the spotlight should land for each step.
// - target: CSS selector to find in the DOM. Null = concept-only centered card.
// - route:  page to navigate to before the step renders. Null = current page is fine.
// - placement: preferred tooltip side relative to the target. Falls back to others
//   automatically if the preferred side clips the viewport.
// Keep keys aligned with TUTORIAL_SECTIONS — missing entries default to concept-only.
export const STEP_META = {
  getting_started: {
    intro:   { target: null,                              route: '/app',        placement: 'bottom' },
    sidebar: { target: '[data-tour="sidebar"]',           route: null,          placement: 'right'  },
    search:  { target: '[data-tour="search-trigger"]',    route: null,          placement: 'bottom' },
  },
  tasks: {
    quick_add: { target: '[data-tour="quick-add"]', route: '/app/tasks', placement: 'bottom' },
    nlp:       { target: '[data-tour="quick-add"]', route: '/app/tasks', placement: 'bottom', visual: 'nlp_demo' },
    labels:    { target: null,                      route: '/app/tasks', placement: 'bottom', visual: 'label_chips' },
  },
  projects_views: {
    board_list:       { target: '[data-tour="view-tabs"]', route: '/app/tasks', placement: 'bottom', visual: 'views_demo' },
    calendar_gallery: { target: '[data-tour="view-tabs"]', route: '/app/tasks', placement: 'bottom' },
  },
  planner: {
    drag_drop:     { target: '[data-tour="planner-main"]',   route: '/app/planner', placement: 'left'  },
    auto_schedule: { target: '[data-tour="auto-schedule"]',  route: '/app/planner', placement: 'bottom' },
  },
  habits: {
    create:  { target: '[data-tour="new-habit"]', route: '/app/habits', placement: 'bottom' },
    streaks: { target: null,                      route: '/app/habits', placement: 'bottom', visual: 'streak_demo' },
  },
  focus_time: {
    what_is: { target: null, route: '/app/focus-time', placement: 'bottom' },
    protect: { target: null, route: '/app/focus-time', placement: 'bottom' },
  },
  documents: {
    notes:    { target: '[data-tour="documents-new"]', route: '/app/documents', placement: 'bottom' },
    mindmaps: { target: '[data-tour="documents-new"]', route: '/app/documents', placement: 'bottom' },
  },
  teams: {
    invite: { target: null, route: '/app/teams',                       placement: 'bottom' },
    assign: { target: null, route: '/app/tasks',                       placement: 'bottom' },
  },
  ai_assistant: {
    chat:  { target: null,                          route: '/app/bots',      placement: 'bottom' },
    parse: { target: '[data-tour="quick-add"]',     route: '/app/tasks',     placement: 'bottom' },
  },
  integrations: {
    google_calendar: { target: '[data-tour="integration-google-calendar"]', route: '/app/settings?tab=integrations', placement: 'top' },
    gmail:           { target: '[data-tour="gmail-connect"]',                route: '/app/gmail',                       placement: 'top' },
  },
};

export const stepMeta = (sectionId, stepId) =>
  STEP_META[sectionId]?.[stepId] || { target: null, route: null, placement: 'bottom' };

export const TOTAL_TUTORIAL_STEPS = TUTORIAL_SECTIONS.reduce(
  (sum, s) => sum + s.steps.length,
  0,
);

// Compute progress for a single section given the user's completed-steps map.
export const sectionProgress = (section, progressMap) => {
  const done = (progressMap?.[section.id] || []).filter((stepId) =>
    section.steps.includes(stepId),
  ).length;
  const total = section.steps.length;
  return { done, total, ratio: total > 0 ? done / total : 0 };
};

// Compute overall progress across all sections.
export const overallProgress = (progressMap) => {
  let done = 0;
  for (const section of TUTORIAL_SECTIONS) {
    done += sectionProgress(section, progressMap).done;
  }
  return {
    done,
    total: TOTAL_TUTORIAL_STEPS,
    ratio: TOTAL_TUTORIAL_STEPS > 0 ? done / TOTAL_TUTORIAL_STEPS : 0,
  };
};

export const isAllComplete = (progressMap) =>
  overallProgress(progressMap).done >= TOTAL_TUTORIAL_STEPS;
